import { useEffect, useState } from "react";
import { Users2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useFideicomiso } from "../lib/fideicomiso";
import { Button } from "../components/ui/button";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

export default function ComiteTecnico() {
    const { selected } = useFideicomiso();
    const [sesiones, setSesiones] = useState([]);
    const [partes, setPartes] = useState([]);
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [form, setForm] = useState({ fecha: "", orden_del_dia: "", miembros_convocados: [], quorum_requerido: 1 });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        if (!selected) return;
        const [{ data: s }, { data: p }] = await Promise.all([
            api.get(`/fideicomisos/${selected.id}/comite/sesiones`),
            api.get(`/fideicomisos/${selected.id}/partes`),
        ]);
        setSesiones(s); setPartes(p);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected?.id]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/fideicomisos/${selected.id}/comite/sesiones`, {
                tipo: "ordinaria", fecha: form.fecha, modalidad: "virtual",
                orden_del_dia: form.orden_del_dia.split("\n").filter(Boolean),
                miembros_convocados: form.miembros_convocados,
                quorum_requerido: parseInt(form.quorum_requerido, 10),
            });
            toast.success("Sesión convocada");
            setOpen(false); setForm({ fecha: "", orden_del_dia: "", miembros_convocados: [], quorum_requerido: 1 });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const asistir = async (sesionId, parteId) => {
        try {
            await api.post(`/sesiones/${sesionId}/asistencia`, { parte_id: parteId });
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    };

    const votar = async (sesionId, idx, parteId, voto) => {
        try {
            await api.post(`/sesiones/${sesionId}/votar`, { resolucion_idx: idx, parte_id: parteId, voto });
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    };

    if (!selected) return <div className="p-10 text-muted-foreground text-sm">Selecciona un fideicomiso.</div>;

    const miembrosComite = partes.filter((p) => p.rol === "miembro_comite_tecnico" || p.rol === "delegado_fiduciario");

    return (
        <div className="p-8 lg:p-10 max-w-[1600px] space-y-6" data-testid="comite-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Núcleo · {selected.numero_interno}
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <Users2 className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Comité Técnico
                    </h1>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="nueva-sesion-comite-btn">Convocar sesión</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Convocar sesión de comité</DialogTitle></DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <div><Label>Fecha</Label>
                                <Input type="date" required value={form.fecha}
                                    onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
                            </div>
                            <div><Label>Orden del día (uno por línea)</Label>
                                <Textarea rows={3} required value={form.orden_del_dia}
                                    onChange={(e) => setForm({ ...form, orden_del_dia: e.target.value })} />
                            </div>
                            <div><Label>Miembros convocados</Label>
                                <div className="border border-border rounded-sm p-2 text-sm max-h-40 overflow-y-auto">
                                    {miembrosComite.map((p) => (
                                        <label key={p.id} className="flex items-center gap-2 py-1">
                                            <input type="checkbox"
                                                checked={form.miembros_convocados.includes(p.id)}
                                                onChange={() => setForm((f) => ({
                                                    ...f, miembros_convocados: f.miembros_convocados.includes(p.id)
                                                        ? f.miembros_convocados.filter((x) => x !== p.id)
                                                        : [...f.miembros_convocados, p.id]
                                                }))} />
                                            <span>{p.persona?.nombre_display} ({p.rol})</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div><Label>Quórum requerido</Label>
                                <Input type="number" min="1" value={form.quorum_requerido}
                                    onChange={(e) => setForm({ ...form, quorum_requerido: e.target.value })} />
                            </div>
                            <DialogFooter>
                                <Button type="submit" disabled={busy}>
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Convocar"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {sesiones.map((s) => (
                <div key={s.id} className="kpi-card space-y-3" data-testid={`sesion-${s.id}`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-display text-xl">Sesión #{s.numero_sesion} · {s.fecha}</h3>
                            <div className="text-xs text-muted-foreground">{s.tipo} · {s.modalidad}</div>
                        </div>
                        <div className="text-right text-sm">
                            <div>Quórum: <b>{s.miembros_asistentes?.length || 0}/{s.quorum_requerido}</b></div>
                            <span className={s.estado === "concluida" ? "badge-exito" : "badge-gold"}>{s.estado}</span>
                        </div>
                    </div>

                    <div className="text-xs">
                        <b>Convocados:</b> {s.miembros_convocados.map((mid) => {
                            const p = partes.find((x) => x.id === mid);
                            const asistio = s.miembros_asistentes?.includes(mid);
                            return (
                                <span key={mid} className={`inline-block mr-2 px-2 py-0.5 rounded-sm ${asistio ? "bg-[color:var(--brand-exito)]/10" : "bg-muted"}`}>
                                    {p?.persona?.nombre_display || mid.slice(0, 8)}
                                    {!asistio && s.estado === "convocada" && (
                                        <Button size="sm" variant="ghost" className="h-5 ml-1 text-[10px]"
                                            onClick={() => asistir(s.id, mid)}>marcar asistió</Button>
                                    )}
                                </span>
                            );
                        })}
                    </div>

                    {s.resoluciones?.length > 0 && (
                        <div className="border-t border-border pt-3">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Resoluciones</div>
                            {s.resoluciones.map((r) => (
                                <div key={r.idx} className="border border-border rounded-sm p-3 mb-2">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="font-medium text-sm">{r.tema}</div>
                                            <div className="text-xs text-muted-foreground">{r.propuesta}</div>
                                        </div>
                                        <div className="text-xs">
                                            <span className={r.aprobada ? "badge-exito" : r.aprobada === false ? "badge-mock" : "badge-gold"}>
                                                {r.aprobada === true ? "aprobada" : r.aprobada === false ? "rechazada" : "pendiente"}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-xs mt-2 flex gap-4">
                                        <span>favor: <b>{r.votos?.filter((v) => v.voto === "favor").length || 0}</b></span>
                                        <span>contra: <b>{r.votos?.filter((v) => v.voto === "contra").length || 0}</b></span>
                                        <span>abst.: <b>{r.votos?.filter((v) => v.voto === "abstencion").length || 0}</b></span>
                                    </div>
                                    {s.estado === "en_sesion" && s.miembros_asistentes?.length >= s.quorum_requerido && (
                                        <div className="mt-2 flex gap-1 flex-wrap">
                                            {s.miembros_asistentes.map((mid) => (
                                                <div key={mid} className="flex gap-1 items-center">
                                                    <span className="text-[10px]">{partes.find((p) => p.id === mid)?.persona?.nombre_display?.split(" ")[0]}:</span>
                                                    {["favor", "contra", "abstencion"].map((v) => (
                                                        <Button key={v} size="sm" variant="ghost" className="h-6 text-[10px]"
                                                            onClick={() => votar(s.id, r.idx, mid, v)}
                                                            data-testid={`votar-${r.idx}-${v}`}>{v}</Button>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
            {sesiones.length === 0 && <div className="text-sm text-muted-foreground">Sin sesiones convocadas.</div>}
        </div>
    );
}
